import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography } from '@material-ui/core';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';

export const REPO_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_.-]{0,99}$/;

export interface RepoNamePickerProps {
  onChange: (value: string) => void;
  formData?: string;
  required?: boolean;
  rawErrors?: string[];
  formContext?: {
    formData?: {
      name?: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
  idSchema?: {
    $id: string;
  };
  uiSchema?: Record<string, any>;
  schema?: {
    title?: string;
    description?: string;
  };
}

export const RepoNamePickerComponent = (props: RepoNamePickerProps) => {
  const {
    onChange,
    formData,
    required = true,
    rawErrors,
    formContext,
    idSchema,
    schema,
  } = props;

  const catalogName = formContext?.formData?.name?.trim();
  const [userEdited, setUserEdited] = useState(false);
  const lastCatalogNameRef = useRef<string | undefined>(undefined);

  // When opening this step or when catalog name changes:
  // Auto-fill from catalog name if user has NOT manually modified it
  useEffect(() => {
    if (!userEdited && catalogName) {
      if (!formData || formData === lastCatalogNameRef.current) {
        onChange(catalogName);
        lastCatalogNameRef.current = catalogName;
      }
    }
  }, [catalogName, userEdited, formData, onChange]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUserEdited(true);
    onChange(e.target.value);
  };

  const handleResetToCatalog = () => {
    setUserEdited(false);
    if (catalogName) {
      onChange(catalogName);
      lastCatalogNameRef.current = catalogName;
    }
  };

  const hasErrors = rawErrors && rawErrors.length > 0;
  const isDifferentFromCatalog = catalogName && formData && formData !== catalogName;

  return (
    <Box marginY={1}>
      <TextField
        id={idSchema?.$id ?? 'repo-name-picker'}
        label={schema?.title ?? 'Repository Name'}
        placeholder={catalogName || 'e.g. abc-backend'}
        helperText={
          hasErrors
            ? rawErrors[0]
            : schema?.description ??
              'Name of the GitHub repository. Auto-filled from Catalog Name, editable independently.'
        }
        required={required}
        value={formData ?? ''}
        onChange={handleChange}
        fullWidth
        error={Boolean(hasErrors)}
        variant="outlined"
        margin="normal"
      />
      {userEdited && isDifferentFromCatalog && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mt={-0.5} mb={1}>
          <Typography variant="caption" color="textSecondary">
            Custom repository name specified (different from catalog name: <strong>{catalogName}</strong>)
          </Typography>
          <Button
            size="small"
            color="primary"
            onClick={handleResetToCatalog}
            style={{ textTransform: 'none' }}
          >
            Reset to Catalog Name
          </Button>
        </Box>
      )}
    </Box>
  );
};

export const repoNameValidation = (value: any, validation: any) => {
  if (!value) {
    validation.addError('Repository name is required');
    return;
  }
  if (!REPO_NAME_REGEX.test(value)) {
    validation.addError(
      'Repository name must start with an alphanumeric character and contain only alphanumeric, dashes, dots, or underscores (max 100 characters)',
    );
  }
};

export const RepoNamePicker = createFormField({
  name: 'RepoNamePicker',
  component: RepoNamePickerComponent as any,
  validation: repoNameValidation as any,
});
